import {
  Card,
  CardActions,
  CardContent,
  Collapse,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import GetAppIcon from '@material-ui/icons/GetApp';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import Timeline from '@material-ui/lab/Timeline';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import { useGenericStyles } from 'common/genericStyles';
import { RegistroHistoricoResponse, RegistroOcorrenciaResponse } from 'common/genericTypes';
import { Button, MdSpace, SmSpace, TextButton } from 'm2m.components';
import moment from 'moment';
import React, { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { formatDate } from 'utils/date';
import { RootState } from 'store';

const useStyles = makeStyles(() =>
  createStyles({
    timelineItem: {
      '&:before': {
        display: 'none',
      },
    },
    timelineContent: {
      padding: '0px 16px',
    },
  })
);

export enum SuspensaoHistoricoKind {
  Participante = 'PARTICIPANTE',
  InstrumentoFinanceiro = 'INSTRUMENTO_FINANCEIRO',
}

interface SuspensaoHistoricoProps {
  kind: SuspensaoHistoricoKind;
  registroHistoricos?: Array<RegistroHistoricoResponse>;
}

interface RegistroGroup {
  [key: string]: Array<RegistroHistoricoResponse>;
}

const mapStateToProps = (state: RootState) => ({
  registroApi: state.registroApi,
});

type Props = SuspensaoHistoricoProps & ReturnType<typeof mapStateToProps>;

const SuspensaoHistorico: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const {
    registroApi: { getRegistroDocumentDownloadLink },
  } = props;
  const [expandedRegistroId, setExpandedRegistroId] = useState<string>();
  const groupedRegistros = props.registroHistoricos
    ?.filter((_) =>
      [RegistroOcorrenciaResponse.Suspenso, RegistroOcorrenciaResponse.SuspensaoRevogada].includes(_.registroOcorrencia)
    )
    .sort((a, b) => +moment(a.dataDeCriacao) - +moment(b.dataDeCriacao))
    .reduce<RegistroGroup>((acc, curr) => {
      const date = formatDate(curr.dataDeCriacao);
      if (acc[date]) {
        acc[date].push(curr);
      } else {
        acc[date] = [curr];
      }
      return acc;
    }, {});

  const classes = useStyles();
  const genericClasses = useGenericStyles();

  const icons: { [key: string]: JSX.Element } = {
    [RegistroOcorrenciaResponse.Suspenso]: <CancelIcon />,
    [RegistroOcorrenciaResponse.SuspensaoRevogada]: <CheckCircleIcon />,
  };

  const dataName = {
    [SuspensaoHistoricoKind.Participante]: 'Participante',
    [SuspensaoHistoricoKind.InstrumentoFinanceiro]: 'Instrumento Financeiro',
  }[props.kind];

  const descriptions: { [key: string]: string } = {
    [RegistroOcorrenciaResponse.Suspenso]: 'suspenso',
    [RegistroOcorrenciaResponse.SuspensaoRevogada]: 'liberado',
  };

  const getRegistroHistoricoDownloadLink = async (documentId: string): Promise<string> => {
    let result: string = '';
    try {
      result = await getRegistroDocumentDownloadLink(dispatch, documentId, true);
    } catch (error) {}
    return result;
  };

  const handleDownloadRegistroHistoricoDocument = async (documentId: string, name: string) => {
    const url = await getRegistroHistoricoDownloadLink(documentId);
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleToggleMoreDetails = (registroId: string) => {
    setExpandedRegistroId(expandedRegistroId === registroId ? '' : registroId);
  };

  return (
    <>
      <Timeline>
        {groupedRegistros &&
          Object.values(groupedRegistros)?.map((registros, index) => (
            <React.Fragment key={index}>
              <TimelineItem className={classes.timelineItem}>
                <TimelineSeparator>
                  <FiberManualRecordIcon style={{ margin: 2 }} fontSize="small" color="primary" />
                  <TimelineConnector style={{ width: 1, backgroundColor: '#E5E5E5' }} />
                </TimelineSeparator>
                <TimelineContent className={classes.timelineContent}>
                  <Typography variant="body2">
                    {moment(registros?.[0].dataDeCriacao).format('dddd DD/MM/YYYY')}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
              {registros.map((registro) => (
                <TimelineItem className={classes.timelineItem}>
                  <TimelineSeparator>
                    <TimelineConnector style={{ width: 1, backgroundColor: '#E5E5E5' }} />
                    {icons[registro.registroOcorrencia]}
                    <TimelineConnector style={{ width: 1, backgroundColor: '#E5E5E5' }} />
                  </TimelineSeparator>
                  <TimelineContent className={classes.timelineContent}>
                    <Card variant="outlined" style={{ maxWidth: '40%', margin: '10px 0' }}>
                      <CardContent style={{ paddingBottom: 16 }}>
                        <Typography variant="body2" display={'inline'}>
                          {dataName}
                        </Typography>
                        &nbsp;<Typography display={'inline'}>{descriptions[registro.registroOcorrencia]}</Typography>
                        &nbsp;
                      </CardContent>
                      {(registro.justificativa || registro.registroHistoricoDocuments?.length) && (
                        <>
                          <Collapse in={expandedRegistroId === registro.id} timeout="auto" unmountOnExit>
                            <CardContent style={{ paddingTop: 0 }}>
                              <Divider />
                              <SmSpace />
                              <Typography variant="body2">{registro.justificativa}</Typography>
                              <MdSpace />
                              {registro.registroHistoricoDocuments?.map((document, index) => (
                                <>
                                  {!!index && <Divider style={{ margin: '10px 0px' }} />}
                                  <Grid key={document.id} container alignItems={'center'} spacing={2}>
                                    <Grid item>
                                      <InsertDriveFileIcon fontSize={'small'} />
                                    </Grid>
                                    <Grid className={genericClasses.wrapTxt} item md>
                                      <Typography variant="caption">{`${document.fileName} - ${
                                        Math.round((document.size / 1000000) * 100) / 100
                                      } MB`}</Typography>
                                    </Grid>
                                    <Grid item>
                                      <Button
                                        startIcon={<GetAppIcon fontSize={'small'} />}
                                        onClick={() =>
                                          handleDownloadRegistroHistoricoDocument(document.id, document.fileName)
                                        }
                                        variant="text"
                                      >
                                        {'Download'}
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </>
                              ))}
                            </CardContent>
                          </Collapse>
                          <CardActions style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}>
                            <TextButton
                              aria-expanded={expandedRegistroId === registro.id}
                              size="small"
                              onClick={() => handleToggleMoreDetails(registro.id)}
                              endIcon={expandedRegistroId === registro.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            >
                              {expandedRegistroId === registro.id ? 'Menos Informações' : 'Mais Informações'}
                            </TextButton>
                          </CardActions>
                        </>
                      )}
                    </Card>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </React.Fragment>
          ))}
      </Timeline>
    </>
  );
};

export default connect(mapStateToProps)(SuspensaoHistorico);
